import { getQueryClient } from '@data/queries/queryClient'
import { TPage } from '@data/queries/types'
import {
    flattenPaginatedData,
    getFetchMoreCursorPagination,
    getFetchMoreOffsetPagination,
} from '@data/queries/util/pagination'
import {
    FetchInfiniteQueryOptions,
    InfiniteData,
    QueryKey,
    UseInfiniteQueryOptions,
    UseInfiniteQueryResult as UseRqInfiniteQueryResult,
    // eslint-disable-next-line no-restricted-imports
    useInfiniteQuery as useRqInfiniteQuery,
} from '@tanstack/react-query'
import { useMemo } from 'react'

/**
 * The object that each data hook passes to `useInfiniteQuery`
 */
type Options<TEntry, TQueryKey extends QueryKey, TApiError extends ApiError = ApiError<unknown, unknown>> = Omit<
    UseInfiniteQueryOptions<TPage<TEntry>, TApiError, TPage<TEntry>, TPage<TEntry>, TQueryKey, number>,
    'initialPageParam' | 'getNextPageParam'
>

/**
 * The other options that a data hook might accept and pass along to `useInfiniteQuery`. It's the same as `Options` but without `queryKey` and `queryFn`.
 */
export type InfiniteQueryOptions<
    TEntry,
    TQueryKey extends QueryKey,
    TApiError extends ApiError = ApiError<unknown, unknown>,
> = Omit<Options<TEntry, TQueryKey, TApiError>, 'queryKey' | 'queryFn'>

export type InfiniteQueryResult<
    TEntry,
    TQueryKey extends QueryKey,
    TApiError extends ApiError = ApiError<unknown, unknown>,
    Facets extends string = string,
> = Omit<UseRqInfiniteQueryResult<TPage<TEntry>, TApiError>, 'data'> & {
    data: ReadonlyArray<TEntry>
    serverTotal: number
    queryKey: TQueryKey
    facets?: TFacets<Facets>
}

function getNextPageParam<TEntry>(lastPage: TPage<TEntry>) {
    if (!lastPage) {
        return undefined
    }

    if (lastPage.paginationType === 'offset') {
        return getFetchMoreOffsetPagination(lastPage)
    }

    return getFetchMoreCursorPagination(lastPage)
}

export function useInfiniteQuery<
    TEntry,
    TQueryKey extends QueryKey,
    TApiError extends ApiError = ApiError<unknown, unknown>,
    Facets extends string = string,
>(options: Options<TEntry, TQueryKey, TApiError>): InfiniteQueryResult<TEntry, TQueryKey, TApiError, Facets> {
    const config = useMemo(
        () => ({
            initialPageParam: 0,
            ...options,
            getNextPageParam,
        }),
        [options]
    )
    const rqResult = useRqInfiniteQuery(config)

    // We have to cast here because we modify the shape in our response mappers.
    // Keep in mind that most API data is validated by schemas, so the risk from casting the type is smaller than usual.
    const pages = (rqResult.data as InfiniteData<TPage<TEntry>, number> | undefined)?.pages

    let serverTotal = 0
    let facets: TFacets<Facets> | undefined

    if (pages) {
        const lastPage = pages[pages.length - 1]

        if (lastPage?.paginationType === 'offset') {
            serverTotal = lastPage.total
        }

        if (pages.length > 0 && pages[0]) {
            facets = pages[0].facets
        }
    }

    const result: InfiniteQueryResult<TEntry, TQueryKey, TApiError, Facets> = useMemo(
        () => ({
            ...rqResult,
            data: flattenPaginatedData(pages),
            queryKey: options.queryKey,
            serverTotal,
            facets,
        }),
        [facets, options.queryKey, pages, rqResult, serverTotal]
    )

    return result
}

export async function prefetchInfiniteQuery<TEntry, TQueryKey extends QueryKey>(
    options: Omit<
        FetchInfiniteQueryOptions<TPage<TEntry>, ApiError, TPage<TEntry>, TQueryKey, number>,
        'initialPageParam'
    >
): Promise<void> {
    return await getQueryClient().prefetchInfiniteQuery<TPage<TEntry>, ApiError, TPage<TEntry>, TQueryKey, number>({
        ...options,
        initialPageParam: 0,
        getNextPageParam,
    })
}
